import React, { useState, useEffect } from 'react'

const VerificationModal = ({ isOpen, onClose, userData, onVerificationSuccess }) => {
  const [nin, setNin] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [step, setStep] = useState('form') // 'form', 'processing'

  // Reset modal state when opened
  useEffect(() => {
    if (isOpen) {
      // Reset all states when modal opens
      setNin('')
      setLoading(false)
      setError('')
      setSuccess('')
      setStep('form')
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate NIN
    if (!nin || nin.length < 11) {
      setError('Please enter a valid 11-digit NIN')
      return
    }

    if (nin.length !== 11) {
      setError('NIN must be exactly 11 digits')
      return
    }

    // Check if user has sufficient balance
    if (parseFloat(userData?.wallet_balance || 0) < 100) {
      setError('Insufficient balance. You need at least ₦100 to verify your NIN.')
      return
    }

    setError('')
    setLoading(true)
    setStep('processing')

    try {
      const token = localStorage.getItem('token')
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.')
      }

      const myHeaders = new Headers()
      myHeaders.append("Content-Type", "application/json")
      myHeaders.append("Accept", "application/json")
      myHeaders.append("Authorization", `Bearer ${token}`)

      const raw = JSON.stringify({
        "nin": nin
      })

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      }

      const response = await fetch("https://devapi.quickcare.ng/api/v1/user/verify-nin", requestOptions)
      const result = await response.json()

      if (response.ok && result.success) {
        // Verification successful
        setSuccess('NIN verification successful! Your account is now verified.')
        onVerificationSuccess('NIN verification successful! Your account is now verified.')
        
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose()
        }, 2000)
      } else {
        // Handle verification failure
        const errorMessage = result.detail || result.message || 'NIN verification failed. Please try again later.'
        throw new Error(errorMessage)
      }
    } catch (err) {
      console.error('Verification error:', err)
      setError(err.message || 'An error occurred during verification. Please try again.')
      setStep('form')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    // Don't allow closing while processing
    if (!loading) {
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-sm lg:text-xl font-bold text-gray-800">
            Compliance Verification
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          {step === 'form' && (
            <form onSubmit={handleSubmit}>
              {/* Information Banner */}
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-xs lg:text-sm font-medium text-blue-800">
                      Important Information
                    </h3>
                    <div className="mt-1 text-xs lg:text-sm space-y-1 text-blue-700">
                      <p>• Compliance verification is required to enable withdrawals</p>
                      <p>• A fee of ₦100 will be deducted from your wallet</p>
                      <p>• Please ensure your NIN is correct before proceeding</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Balance Display */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-sm lg:text-base text-gray-600">Wallet Balance:</span>
                  <span className="lg:text-xl font-bold text-green-600">
                    ₦{parseFloat(userData?.wallet_balance || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200">
                  <span className="text-sm lg:text-base text-gray-600">Verification Fee:</span>
                  <span className="text-sm lg:text-lg font-semibold text-gray-800">₦100</span>
                </div>
                {parseFloat(userData?.wallet_balance || 0) < 100 && (
                  <div className="mt-2 p-2 bg-red-50 rounded">
                    <p className="text-xs text-red-600">
                      ⚠️ Insufficient balance. You need at least ₦100 to verify.
                    </p>
                  </div>
                )}
              </div>

              {/* NIN Input */}
              <div className="mb-6">
                <label htmlFor="nin" className="block text-xs lg:text-sm font-medium text-gray-700 mb-2">
                  National Identification Number (NIN) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="nin"
                  value={nin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '')
                    if (value.length <= 11) {
                      setNin(value)
                    }
                  }}
                  placeholder="Enter your 11-digit NIN"
                  maxLength="11"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the 11-digit number on your NIN slip
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-start">
                    <svg className="h-4 w-4 text-red-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-xs lg:text-sm text-red-600">{error}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-sm lg:text-base"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || parseFloat(userData?.wallet_balance || 0) < 100}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-md transition-colors text-sm lg:text-base"
                >
                  Verify NIN 
                </button>
              </div>
            </form>
          )}

          {step === 'processing' && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {loading ? 'Verifying Your NIN...' : 'Processing...'}
              </h3>
              <p className="text-gray-600">
                {loading ? 'Please wait while we verify your NIN.' : 'This may take a few seconds...'}
              </p>
              
              {/* Success Message */}
              {success && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-center justify-center">
                    <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-green-600">{success}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VerificationModal